import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import {
  FileText,
  Package,
  Users,
  DollarSign,
  BarChart3,
  ShoppingCart,
  CreditCard,
  UserCog,
  Calculator,
  FolderKanban,
  Warehouse,
  Factory,
  Truck,
  Stethoscope,
  GraduationCap,
  Wrench,
  TrendingDown,
  Receipt,
  Scale,
  Lock,
  Briefcase,
  ClipboardList,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

export const Route = createFileRoute('/apps/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: AppsPage,
});

// Map app IDs to feature_definitions codes (where they differ)
const appToFeatureCode: Record<string, string> = {
  sales: 'invoices',
  purchasing: 'suppliers',
};

interface App {
  id: string;
  name: string;
  nameAr: string;
  nameFr: string;
  description: string;
  icon: any;
  color: string;
  route: string;
  hardEnabled: boolean; // fallback if feature flags haven't loaded
  tier: 1 | 2 | 3 | 4;
  badge?: string;
}

const APPS: App[] = [
  // Tier 1: Core Apps (Ready)
  {
    id: 'sales',
    name: 'المبيعات / Ventes',
    nameAr: 'المبيعات',
    nameFr: 'Ventes',
    description: 'Devis, factures, et cycle de vente complet',
    icon: FileText,
    color: 'from-emerald-500 to-teal-600',
    route: '/apps/sales',
    hardEnabled: true,
    tier: 1,
  },
  {
    id: 'quotes',
    name: 'عروض الأسعار / Devis',
    nameAr: 'عروض الأسعار',
    nameFr: 'Devis',
    description: 'Gestion des devis et conversion en factures',
    icon: ClipboardList,
    color: 'from-violet-500 to-purple-600',
    route: '/apps/quotes',
    hardEnabled: true,
    tier: 1,
    badge: 'NEW',
  },
  {
    id: 'products',
    name: 'المنتجات / Produits',
    nameAr: 'المنتجات',
    nameFr: 'Produits',
    description: 'Catalogue produits et gestion du stock',
    icon: Package,
    color: 'from-orange-500 to-amber-600',
    route: '/apps/products',
    hardEnabled: true,
    tier: 1,
  },
  {
    id: 'services',
    name: 'الخدمات / Services',
    nameAr: 'الخدمات',
    nameFr: 'Services',
    description: 'Gestion des services et prestations',
    icon: Briefcase,
    color: 'from-blue-500 to-indigo-600',
    route: '/apps/services',
    hardEnabled: true,
    tier: 1,
    badge: 'NEW',
  },
  {
    id: 'clients',
    name: 'العملاء / Clients',
    nameAr: 'العملاء',
    nameFr: 'Clients',
    description: 'Gestion des clients et relations',
    icon: Users,
    color: 'from-blue-500 to-cyan-600',
    route: '/apps/clients',
    hardEnabled: true,
    tier: 1,
  },
  {
    id: 'suppliers',
    name: 'الموردون / Fournisseurs',
    nameAr: 'الموردون',
    nameFr: 'Fournisseurs',
    description: 'Gestion des fournisseurs et achats',
    icon: TrendingDown,
    color: 'from-orange-500 to-red-600',
    route: '/apps/suppliers',
    hardEnabled: true,
    tier: 1,
    badge: 'NEW',
  },

  // Tier 2: Critical Apps
  {
    id: 'treasury',
    name: 'الخزينة / Trésorerie',
    nameAr: 'الخزينة',
    nameFr: 'Trésorerie',
    description: 'Gestion de trésorerie, CCP, et paiements',
    icon: DollarSign,
    color: 'from-green-500 to-emerald-600',
    route: '/apps/treasury',
    hardEnabled: true,
    tier: 2,
    badge: 'NEW',
  },
  {
    id: 'checks',
    name: 'الشيكات / Chèques',
    nameAr: 'الشيكات',
    nameFr: 'Chèques',
    description: 'Gestion des chèques (180 jours)',
    icon: Receipt,
    color: 'from-teal-500 to-cyan-600',
    route: '/apps/treasury/checks',
    hardEnabled: true,
    tier: 2,
    badge: 'NEW',
  },
  {
    id: 'reports',
    name: 'التقارير / Rapports',
    nameAr: 'التقارير',
    nameFr: 'Rapports',
    description: 'Rapports, statistiques, et G50',
    icon: BarChart3,
    color: 'from-purple-500 to-violet-600',
    route: '/apps/reports',
    hardEnabled: true,
    tier: 2,
    badge: 'NEW',
  },
  {
    id: 'compliance',
    name: 'الامتثال / Conformité',
    nameAr: 'الامتثال',
    nameFr: 'Conformité',
    description: 'Obligations fiscales et État 104',
    icon: Scale,
    color: 'from-blue-600 to-indigo-700',
    route: '/apps/compliance',
    hardEnabled: true,
    tier: 2,
    badge: 'NEW',
  },
  {
    id: 'pos',
    name: 'نقطة البيع / POS',
    nameAr: 'نقطة البيع',
    nameFr: 'Point de Vente',
    description: 'Caisse enregistreuse moderne',
    icon: CreditCard,
    color: 'from-pink-500 to-rose-600',
    route: '/apps/pos',
    hardEnabled: true,
    tier: 2,
    badge: 'NEW',
  },
  {
    id: 'purchasing',
    name: 'المشتريات / Achats',
    nameAr: 'المشتريات',
    nameFr: 'Achats',
    description: 'Bons de commande et fournisseurs',
    icon: ShoppingCart,
    color: 'from-indigo-500 to-blue-600',
    route: '/apps/purchasing',
    hardEnabled: false,
    tier: 2,
    badge: 'SOON',
  },

  // Tier 3: Advanced Apps
  {
    id: 'hr',
    name: 'الموارد البشرية / RH',
    nameAr: 'الموارد البشرية',
    nameFr: 'Ressources Humaines',
    description: 'Paie, CNAS, et gestion du personnel',
    icon: UserCog,
    color: 'from-red-500 to-orange-600',
    route: '/apps/hr',
    hardEnabled: false,
    tier: 3,
  },
  {
    id: 'accounting',
    name: 'المحاسبة / Comptabilité',
    nameAr: 'المحاسبة',
    nameFr: 'Comptabilité',
    description: 'Plan comptable PCN et écritures',
    icon: Calculator,
    color: 'from-teal-500 to-cyan-600',
    route: '/apps/accounting',
    hardEnabled: true,
    tier: 3,
    badge: 'NEW',
  },
  {
    id: 'projects',
    name: 'المشاريع / Projets',
    nameAr: 'المشاريع',
    nameFr: 'Projets',
    description: 'Gestion de projets et tâches',
    icon: FolderKanban,
    color: 'from-violet-500 to-purple-600',
    route: '/apps/projects',
    hardEnabled: false,
    tier: 3,
  },
  {
    id: 'inventory',
    name: 'المخزون / Stock Avancé',
    nameAr: 'المخزون',
    nameFr: 'Stock Avancé',
    description: 'Multi-dépôts et traçabilité',
    icon: Warehouse,
    color: 'from-yellow-500 to-orange-600',
    route: '/apps/inventory',
    hardEnabled: false,
    tier: 3,
  },

  // Tier 4: Industry Apps
  {
    id: 'manufacturing',
    name: 'الإنتاج / Production',
    nameAr: 'الإنتاج',
    nameFr: 'Production',
    description: 'Fabrication et nomenclatures',
    icon: Factory,
    color: 'from-gray-500 to-slate-600',
    route: '/apps/manufacturing',
    hardEnabled: false,
    tier: 4,
  },
  {
    id: 'logistics',
    name: 'اللوجستيك / Logistique',
    nameAr: 'اللوجستيك',
    nameFr: 'Logistique',
    description: 'Livraisons et transport',
    icon: Truck,
    color: 'from-cyan-500 to-blue-600',
    route: '/apps/logistics',
    hardEnabled: false,
    tier: 4,
  },
  {
    id: 'healthcare',
    name: 'الصحة / Santé',
    nameAr: 'الصحة',
    nameFr: 'Santé',
    description: 'Pharmacies et cabinets médicaux',
    icon: Stethoscope,
    color: 'from-red-400 to-pink-600',
    route: '/apps/healthcare',
    hardEnabled: false,
    tier: 4,
  },
  {
    id: 'education',
    name: 'التعليم / Éducation',
    nameAr: 'التعليم',
    nameFr: 'Éducation',
    description: 'Écoles et établissements',
    icon: GraduationCap,
    color: 'from-blue-400 to-indigo-600',
    route: '/apps/education',
    hardEnabled: false,
    tier: 4,
  },
  {
    id: 'workshop',
    name: 'الورشة / Garage',
    nameAr: 'الورشة',
    nameFr: 'Garage Auto',
    description: 'Réparation automobile',
    icon: Wrench,
    color: 'from-slate-500 to-gray-600',
    route: '/apps/workshop',
    hardEnabled: false,
    tier: 4,
  },
];

function AppsPage() {
  const { company } = useAuthStore();
  const { isFeatureEnabled, isLoading: featuresLoading } = useFeatureFlags();

  const resolveEnabled = (app: App): boolean => {
    // Always show the hardcoded state while feature flags are loading
    if (featuresLoading) return app.hardEnabled;
    // Map app id to feature code
    const featureCode = appToFeatureCode[app.id] || app.id;
    // Check feature flags; fall back to hardEnabled for apps not in DB
    return isFeatureEnabled(featureCode) || app.hardEnabled;
  };

  const tiers = [
    { id: 1, name: 'Applications Principales', nameAr: 'التطبيقات الأساسية', apps: APPS.filter((a) => a.tier === 1) },
    { id: 2, name: 'Applications Critiques', nameAr: 'التطبيقات الحرجة', apps: APPS.filter((a) => a.tier === 2) },
    { id: 3, name: 'Applications Avancées', nameAr: 'التطبيقات المتقدمة', apps: APPS.filter((a) => a.tier === 3) },
    { id: 4, name: 'Applications Sectorielles', nameAr: 'التطبيقات القطاعية', apps: APPS.filter((a) => a.tier === 4) },
  ];

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-background p-8">
        {/* Header */}
        <div className="max-w-7xl mx-auto mb-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-foreground mb-4">
              leBusiness
            </h1>
            <p className="text-xl text-muted-foreground">
              {company?.name || 'شركتك / Votre Entreprise'}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              اختر التطبيق للبدء / Choisissez une application pour commencer
            </p>
          </div>

          {/* Apps Grid by Tier */}
          {tiers.map((tier) => (
            <div key={tier.id} className="mb-12">
              <h2 className="text-2xl font-semibold text-foreground mb-6 flex items-center gap-3">
                <span className="w-1 h-8 bg-gradient-to-b from-emerald-500 to-teal-600 rounded-full" />
                <span>{tier.nameAr} / {tier.name}</span>
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                {tier.apps.map((app) => {
                  const Icon = app.icon;
                  const isEnabled = resolveEnabled(app);

                  const AppCard = (
                    <div
                      className={`
                        relative group rounded-2xl p-6 transition-all duration-300
                        ${
                          isEnabled
                            ? 'bg-card hover:shadow-2xl hover:scale-105 cursor-pointer border border-border'
                            : 'bg-muted cursor-not-allowed opacity-60'
                        }
                      `}
                    >
                      {/* Badge */}
                      {app.badge && (
                        <div className="absolute top-3 right-3">
                          <span
                            className={`
                              px-2 py-1 text-xs font-bold rounded-full
                              ${
                                app.badge === 'NEW'
                                  ? 'bg-emerald-500 text-white'
                                  : 'bg-orange-500 text-white'
                              }
                            `}
                          >
                            {app.badge}
                          </span>
                        </div>
                      )}

                      {/* Icon */}
                      <div className="mb-4">
                        <div
                          className={`
                            w-16 h-16 rounded-xl flex items-center justify-center
                            bg-gradient-to-br ${app.color}
                            ${isEnabled ? 'group-hover:scale-110' : ''}
                            transition-transform duration-300
                          `}
                        >
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                      </div>

                      {/* Name */}
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {app.nameFr}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-1">{app.nameAr}</p>

                      {/* Description */}
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {app.description}
                      </p>

                      {/* Disabled Overlay */}
                      {!isEnabled && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-card/80 rounded-2xl gap-1">
                          {app.hardEnabled ? (
                            <>
                              <Lock className="w-5 h-5 text-muted-foreground" />
                              <span className="text-xs font-semibold text-muted-foreground">
                                Premium
                              </span>
                            </>
                          ) : (
                            <span className="text-sm font-semibold text-muted-foreground">
                              قريباً / Bientôt
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );

                  return isEnabled ? (
                    <Link key={app.id} to={app.route}>
                      {AppCard}
                    </Link>
                  ) : (
                    <div key={app.id}>{AppCard}</div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
